import { fnv1a } from '../track/rng';
import { bytesToBase64 } from './codec';
import type { RunResult } from '../game/session';

export const CLIENT_VERSION = 1;

/**
 * The payload a leaderboard server would accept.
 *
 * Defined now, while nothing sends it, so adding a backend later is wiring
 * rather than a refactor of everything that touches a lap time.
 *
 * The security property lives entirely in `traceB64`: it is the full input
 * record, one byte per 120 Hz tick, so a server can re-run it through the same
 * deterministic sim and confirm the claimed time actually falls out. `checksum`
 * is only an integrity check against truncation and accidental corruption — it
 * is computed on the client and is trivially forgeable, so it must never be
 * treated as proof of anything.
 *
 * One caveat for whoever builds that server: ECMAScript does not pin down the
 * exact results of Math.atan2/sin/cos/tan, so a re-simulation on a different
 * engine can drift by an ulp and diverge slightly over a lap. Validate against a
 * tolerance — time within a few ms and the trajectory staying on track — not
 * bit-for-bit equality.
 */
export interface Submission {
  version: number;
  trackKey: string;
  trackSeed: number;
  timeMs: number;
  checkpointsHit: number;
  checkpointsTotal: number;
  livery: string;
  /**
   * Realism mode. Part of the replay context, not decoration: the same input
   * trace produces a different lap under different assists, so a server cannot
   * re-simulate a submission without it. Also the key the leaderboard shards on,
   * since brake assist lets you brake later than Sport ever could.
   */
  mode: string;
  tickHz: number;
  traceB64: string;
  /** Additive drag channel — one quantised byte per tick, alongside `trace`.
   *  Optional: absent on any client that predates it, and the server treats
   *  an absent channel as drag 1 throughout, same as before it existed. */
  dragB64?: string;
  checksum: string;
}

export function buildSubmission(
  trackKey: string,
  trackSeed: number,
  livery: string,
  mode: string,
  result: RunResult,
  tickHz: number,
): Submission {
  const traceB64 = bytesToBase64(result.trace);
  const dragB64 = bytesToBase64(result.dragTrace);
  const body = [
    CLIENT_VERSION,
    trackKey,
    trackSeed,
    Math.round(result.timeMs * 1000),
    result.checkpointsHit,
    result.checkpointsTotal,
    livery,
    mode,
    tickHz,
    traceB64,
  ].join('|');

  return {
    version: CLIENT_VERSION,
    trackKey,
    trackSeed,
    timeMs: result.timeMs,
    checkpointsHit: result.checkpointsHit,
    checkpointsTotal: result.checkpointsTotal,
    livery,
    mode,
    tickHz,
    traceB64,
    dragB64,
    checksum: fnv1a(body).toString(16).padStart(8, '0'),
  };
}
